import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Pencil, ArrowUpRight, ArrowDownRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtEUR, fmtDate, currentMonth, INCOME, EXPENSE } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function History() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(currentMonth());
  const [type, setType] = useState("all");
  const [scope, setScope] = useState("all");
  const [editing, setEditing] = useState(null);

  const params = new URLSearchParams({ month });
  if (type !== "all") params.set("type", type);
  if (scope !== "all") params.set("scope", scope);

  const { data: txns = [] } = useQuery({
    queryKey: ["transactions", month, type, scope],
    queryFn: () => api.get(`/transactions?${params.toString()}`).then((r) => r.data),
  });
  const { data: incomeCats = [] } = useQuery({
    queryKey: ["categories", "income"],
    queryFn: () => api.get("/categories?type=income").then((r) => r.data),
  });
  const { data: expenseCats = [] } = useQuery({
    queryKey: ["categories", "expense"],
    queryFn: () => api.get("/categories?type=expense").then((r) => r.data),
  });

  const del = async (id) => {
    if (!window.confirm("Apagar esta transação?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Transação apagada");
    qc.invalidateQueries();
  };

  const saveEdit = async () => {
    try {
      await api.put(`/transactions/${editing.id}`, {
        type: editing.type,
        amount: parseFloat(String(editing.amount).replace(",", ".")),
        category: editing.category || null,
        account_id: editing.account_id,
        date: editing.date,
        description: editing.description || "",
        scope: editing.scope,
        status: editing.status || "confirmado",
      });
      toast.success("Transação atualizada");
      setEditing(null);
      qc.invalidateQueries();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const cats = editing?.type === "income" ? incomeCats : expenseCats;

  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl font-bold">Histórico</h1>

      <div className="flex flex-wrap gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="filter-month" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-36" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="income">Recebido</SelectItem>
            <SelectItem value="expense">Despesas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-36" data-testid="filter-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os âmbitos</SelectItem>
            <SelectItem value="pessoal">Pessoal</SelectItem>
            <SelectItem value="empresa">Empresa</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="transactions-list">
        {txns.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">Sem transações neste período.</p>
        ) : (
          txns.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-3" data-testid={`tx-row-${t.id}`}>
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: (t.type === "income" ? INCOME : EXPENSE) + "1a" }}
              >
                {t.type === "income" ? (
                  <ArrowUpRight size={18} style={{ color: INCOME }} />
                ) : (
                  <ArrowDownRight size={18} style={{ color: EXPENSE }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{t.category || "Sem categoria"}</span>
                  {!t.category && <Badge variant="outline" className="text-[#B8860B] border-[#D9A566]">rever</Badge>}
                  {t.scope === "empresa" && <Badge variant="outline" className="text-[#C75D42] border-[#C75D42]">empresa</Badge>}
                  {t.status === "por_confirmar" && <Badge variant="outline">por confirmar</Badge>}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {fmtDate(t.date)} · {t.account_name || "—"}{t.description ? ` · ${t.description}` : ""}
                </div>
              </div>
              <div className="font-heading font-bold" style={{ color: t.type === "income" ? INCOME : EXPENSE }}>
                {t.type === "income" ? "+" : "−"}{fmtEUR(t.amount)}
              </div>
              <button onClick={() => setEditing({ ...t })} className="text-muted-foreground hover:text-primary p-1" data-testid={`tx-edit-${t.id}`}>
                <Pencil size={16} />
              </button>
              <button onClick={() => del(t.id)} className="text-muted-foreground hover:text-[#C75D42] p-1" data-testid={`tx-delete-${t.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-dialog">
          <DialogHeader><DialogTitle>Editar transação</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Montante</Label>
                  <Input value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid="edit-amount" />
                </div>
                <div>
                  <Label>Data</Label>
                  <Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid="edit-date" />
                </div>
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={editing.category || ""} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="edit-category"><SelectValue placeholder="Sem categoria" /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Âmbito</Label>
                <Select value={editing.scope} onValueChange={(v) => setEditing({ ...editing, scope: v })}>
                  <SelectTrigger data-testid="edit-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pessoal">Pessoal</SelectItem>
                    <SelectItem value="empresa">Empresa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descrição</Label>
                <Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="edit-description" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} data-testid="edit-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
